/**
 * UniRankingHero — bold hero card that lands ABOVE the description on
 * a uni page whenever that uni ranks #1-#3 in a WUA subject × country
 * leaderboard. Turns the abstract "prestige" question into a concrete,
 * source-cited leaderboard position — the FIRST thing families see.
 *
 * Falls back to null if no top-3 appearance exists, so the layout is
 * self-hiding for mid-tier unis (the small ranking chips below the
 * description continue to surface any 4-20 positions).
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Trophy, ArrowRight } from "lucide-react";
import { subjectSlug } from "@/lib/rankingSlug";

const COUNTRY_LABEL = {
  UK: "the UK", US: "the US", AU: "Australia", CA: "Canada",
  NL: "the Netherlands", FR: "France", DE: "Germany", IE: "Ireland",
  IT: "Italy", ES: "Spain", DK: "Denmark", SE: "Sweden", NO: "Norway",
};

export default function UniRankingHero({ uniId }) {
  const [items, setItems] = useState(null);
  useEffect(() => {
    if (!uniId) return;
    api
      .get(`/rankings/uni-appearances/${uniId}?top_n=20`)
      .then((r) => setItems(r.data.appearances || []))
      .catch(() => setItems([]));
  }, [uniId]);

  if (!items) return null;
  const topThree = items.filter((a) => a.rank <= 3);
  if (topThree.length === 0) return null;

  const number1 = topThree.filter((a) => a.rank === 1);
  const highlight = number1[0] || topThree[0]; // best card
  const others = topThree.filter((a) => a !== highlight);

  return (
    <div
      className="mt-6 rounded-md border border-amber-300 bg-gradient-to-br from-amber-50 via-white to-white p-5 sm:p-6"
      data-testid="uni-ranking-hero"
    >
      <div className="flex flex-wrap items-start gap-x-6 gap-y-3">
        <div className="flex-1 min-w-[280px]">
          <div className="flex items-center gap-2 text-amber-800">
            <Trophy className="h-4 w-4" />
            <span className="label-uppercase !text-[10px] !text-amber-800">
              Ranked #{highlight.rank} for {highlight.subject} in {COUNTRY_LABEL[highlight.country]}
            </span>
          </div>
          <div className="mt-2 flex items-baseline gap-3">
            <span
              className="font-chip font-bold text-4xl sm:text-5xl text-amber-600"
              data-testid="uni-ranking-hero-rank"
            >
              #{highlight.rank}
            </span>
            <span className="font-editorial text-2xl sm:text-3xl text-[#002147]">
              {highlight.subject}
            </span>
          </div>
          <p className="mt-2 text-sm text-stone-700 leading-relaxed">
            Out of {highlight.total_qualified} {COUNTRY_LABEL[highlight.country]} universities
            we could rank with verified per-uni-per-subject graduate salary data. Composite
            score {highlight.score}/100 — driven by outcomes, value for money, admissibility,
            satisfaction and retention. No opinion polls, no licensed prestige scores.
          </p>
          <Link
            to={`/rankings/${highlight.country.toLowerCase()}/${subjectSlug(highlight.subject)}`}
            data-testid="uni-ranking-hero-cta"
            className="mt-3 inline-flex items-center gap-1.5 text-sm font-semibold text-amber-700 hover:text-amber-900"
          >
            See the full leaderboard <ArrowRight className="h-3.5 w-3.5" />
          </Link>
        </div>

        {others.length > 0 && (
          <div className="min-w-[220px] pl-6 border-l border-amber-200 space-y-2">
            <div className="text-[10px] font-underground-sc tracking-wider text-stone-500">
              Also ranked here
            </div>
            {others.map((a) => (
              <Link
                key={`${a.country}-${a.subject}`}
                to={`/rankings/${a.country.toLowerCase()}/${subjectSlug(a.subject)}`}
                data-testid={`uni-ranking-hero-other-${subjectSlug(a.subject)}`}
                className="flex items-baseline gap-2 hover:bg-white rounded px-2 py-1 -mx-2 transition-colors"
              >
                <span className="font-mono font-bold text-[#4C1D95] text-sm">
                  #{a.rank}
                </span>
                <span className="text-sm text-stone-700 truncate">
                  {a.subject}
                </span>
                <span className="ml-auto text-[10px] font-mono text-stone-400">
                  {a.country}
                </span>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
